import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, TriangleAlert, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { AppWithStats, ImportPreviewResponse, ImportRow } from '@/core/types';
import { api } from '@/lib/api';
import { csvRowToImportRow, parseCSV } from '@/lib/csv';

type Props = { apps: AppWithStats[] };

export function ImportSection({ apps }: Props) {
  const activeApps = apps.filter((a) => a.status === 'active');
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>(activeApps.map((a) => a.id));
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [parsedRows, setParsedRows] = useState<ImportRow[]>([]);
  const [keptNew, setKeptNew] = useState<Set<number>>(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const previewMutation = useMutation({
    mutationFn: api.previewImport,
    onSuccess: (data) => {
      setPreview(data);
      setKeptNew(new Set(data.new.map((_, i) => i)));
    },
  });

  const commitMutation = useMutation({
    mutationFn: api.commitImport,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      queryClient.invalidateQueries({ queryKey: ['stats', 'overall'] });
      setPreview(null);
      setParsedRows([]);
      setUploadError(null);
      toast.success(`Imported ${data.imported} word${data.imported === 1 ? '' : 's'}`);
    },
    onError: (err: Error) => toast.error('Import failed', { description: err.message }),
  });

  const handleFile = async (file: File) => {
    setUploadError(null);
    try {
      const text = await file.text();
      const { rows } = parseCSV(text);
      const importRows = rows.map(csvRowToImportRow).filter((r) => r.de);
      if (importRows.length === 0) {
        setUploadError('No valid rows found in CSV. Did the file have a header row?');
        return;
      }
      setParsedRows(importRows);
      previewMutation.mutate({ app_ids: selectedAppIds, rows: importRows });
    } catch (err) {
      setUploadError((err as Error).message);
    }
  };

  const toggleApp = (appId: string) => {
    setSelectedAppIds((ids) =>
      ids.includes(appId) ? ids.filter((id) => id !== appId) : [...ids, appId],
    );
  };

  const toggleKeep = (idx: number) => {
    setKeptNew((s) => {
      const next = new Set(s);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const onCommit = () => {
    if (!preview) return;
    const rowsToCommit = preview.new.filter((_, idx) => keptNew.has(idx));
    if (rowsToCommit.length === 0) return;
    commitMutation.mutate({ app_ids: preview.target_app_ids, rows: rowsToCommit });
  };

  if (preview) {
    return (
      <PreviewView
        preview={preview}
        keptNew={keptNew}
        onToggleKeep={toggleKeep}
        onCommit={onCommit}
        onCancel={() => {
          setPreview(null);
          setParsedRows([]);
        }}
        isCommitting={commitMutation.isPending}
        commitError={commitMutation.error as Error | null}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div>
          <Label className="text-base">1. Pick the app(s) to import into</Label>
          <p className="text-muted-foreground text-xs">
            The same word can be added to multiple apps (each tracks progress independently).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {activeApps.map((a) => (
            <label
              key={a.id}
              className="border-border bg-card flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"
            >
              <input
                type="checkbox"
                checked={selectedAppIds.includes(a.id)}
                onChange={() => toggleApp(a.id)}
              />
              {a.display_name}
            </label>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <Label className="text-base">2. Download a CSV template</Label>
        <div className="flex flex-wrap gap-2">
          {selectedAppIds.map((id) => (
            <Button key={id} asChild variant="outline" size="sm">
              <a href={api.importTemplateUrl(id)} download>
                <Download className="size-4" />
                {activeApps.find((a) => a.id === id)?.display_name ?? id}
              </a>
            </Button>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">
          Fill it in yourself or feed the prompt recipe at{' '}
          <code>docs/word-generation-prompt.md</code> to an LLM.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <Label className="text-base">3. Upload the filled CSV</Label>
        <label className="border-border bg-card hover:bg-muted flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed p-6 text-sm transition-colors">
          <Upload className="text-muted-foreground size-5" />
          <span>Click to choose a CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={selectedAppIds.length === 0 || previewMutation.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
            className="hidden"
          />
        </label>
        {previewMutation.isPending && (
          <p className="text-muted-foreground text-sm">Analyzing {parsedRows.length} rows…</p>
        )}
        {uploadError && <p className="text-destructive text-sm">{uploadError}</p>}
        {previewMutation.error && (
          <p className="text-destructive text-sm">{(previewMutation.error as Error).message}</p>
        )}
      </section>
    </div>
  );
}

function PreviewView({
  preview,
  keptNew,
  onToggleKeep,
  onCommit,
  onCancel,
  isCommitting,
  commitError,
}: {
  preview: ImportPreviewResponse;
  keptNew: Set<number>;
  onToggleKeep: (idx: number) => void;
  onCommit: () => void;
  onCancel: () => void;
  isCommitting: boolean;
  commitError: Error | null;
}) {
  const newCount = keptNew.size;
  return (
    <div className="flex flex-col gap-5">
      <header className="flex items-baseline justify-between">
        <h3 className="font-display text-lg font-semibold">Review import</h3>
        <div className="text-muted-foreground text-sm">
          {preview.new.length} new · {preview.duplicates.length} duplicates ·{' '}
          {preview.warnings.length} warnings
        </div>
      </header>

      {preview.new.length > 0 && (
        <Section title={`New words (${preview.new.length})`}>
          <ul className="divide-border border-border bg-card flex flex-col divide-y rounded-md border">
            {preview.new.map((row, idx) => (
              <li key={idx} className="flex items-center justify-between gap-3 p-3 text-sm">
                <div className="flex flex-1 flex-col">
                  <span className="font-medium">
                    {row.article ?? ''} {row.de}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {[...row.en, ...row.pt].join(' · ')}
                    {row.level && ` · ${row.level}`}
                  </span>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={keptNew.has(idx)}
                    onChange={() => onToggleKeep(idx)}
                  />
                  keep
                </label>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {preview.duplicates.length > 0 && (
        <Section title={`Duplicates (${preview.duplicates.length}) — skipped by default`}>
          <ul className="divide-border border-border bg-card flex flex-col divide-y rounded-md border">
            {preview.duplicates.map((d, idx) => (
              <li key={idx} className="flex items-start gap-3 p-3 text-sm">
                <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <span className="font-medium">{d.row.de}</span>{' '}
                  <span className="text-muted-foreground text-xs">
                    already exists in: {d.existing_app_ids.join(', ')}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {preview.warnings.length > 0 && (
        <Section title={`Warnings (${preview.warnings.length}) — skipped by default`}>
          <ul className="divide-border border-border bg-card flex flex-col divide-y rounded-md border">
            {preview.warnings.map((w, idx) => (
              <li key={idx} className="flex items-start gap-3 p-3 text-sm">
                <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <span className="font-medium">{w.row.de}</span>{' '}
                  <span className="text-muted-foreground text-xs">— {w.issues.join(', ')}</span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onCommit} disabled={newCount === 0 || isCommitting}>
          {isCommitting ? 'Importing…' : `Import ${newCount} word${newCount === 1 ? '' : 's'}`}
        </Button>
        {commitError && <span className="text-destructive text-sm">{commitError.message}</span>}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h4 className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
        {title}
      </h4>
      {children}
    </section>
  );
}
